import { useEffect, useState } from "react";

/**
 * Hook that reads safe area CSS variables from the root element
 * Updates automatically on resize, orientation change, or CSS variable changes.
 */
export const useSafeAreaStyles = (): React.CSSProperties => {
  const [safeAreaStyles, setSafeAreaStyles] = useState<React.CSSProperties>({});

  useEffect(() => {
    const updateSafeAreaStyles = () => {
      const root = document.documentElement;
      const rootStyle = getComputedStyle(root);

      // Read safe area CSS variables from root
      const safeAreaTop =
        rootStyle.getPropertyValue("--safe-area-inset-top").trim() ||
        rootStyle.getPropertyValue("--ion-safe-area-top").trim() ||
        "";

      const safeAreaBottom =
        rootStyle.getPropertyValue("--safe-area-inset-bottom").trim() ||
        rootStyle.getPropertyValue("--ion-safe-area-bottom").trim() ||
        "";

      const safeAreaLeft =
        rootStyle.getPropertyValue("--safe-area-inset-left").trim() ||
        rootStyle.getPropertyValue("--ion-safe-area-left").trim() ||
        "";

      const safeAreaRight =
        rootStyle.getPropertyValue("--safe-area-inset-right").trim() ||
        rootStyle.getPropertyValue("--ion-safe-area-right").trim() ||
        "";

      // Build styles object with CSS custom properties
      const styles: Record<string, string> = {};

      if (safeAreaTop) {
        styles["--safe-area-inset-top"] = safeAreaTop;
      }

      if (safeAreaBottom) {
        styles["--safe-area-inset-bottom"] = safeAreaBottom;
      }

      if (safeAreaLeft) {
        styles["--safe-area-inset-left"] = safeAreaLeft;
      }

      if (safeAreaRight) {
        styles["--safe-area-inset-right"] = safeAreaRight;
      }

      setSafeAreaStyles(styles as React.CSSProperties);
    };

    // Initial update
    updateSafeAreaStyles();

    // Update on resize/orientation change to capture dynamic safe area changes
    window.addEventListener("resize", updateSafeAreaStyles);
    window.addEventListener("orientationchange", updateSafeAreaStyles);

    // Use MutationObserver to watch for changes to CSS variables on root
    const observer = new MutationObserver(() => {
      updateSafeAreaStyles();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      window.removeEventListener("resize", updateSafeAreaStyles);
      window.removeEventListener("orientationchange", updateSafeAreaStyles);
      observer.disconnect();
    };
  }, []);

  return safeAreaStyles;
};
